// src/features/billing/VoidCreditGrantModal.tsx
// Voids an unused credit grant with a required reason (VoidCreditGrant).

import { useState } from 'react';
import { useI18n } from '@react/hooks';
import { Input } from '@react/components';
import { FormDialog } from '@/components/form';
import type { CreditGrant } from '@core/types/billing';
import { formatMoney, errMessage } from './format';

export interface VoidCreditGrantModalProps {
  open: boolean;
  grant: CreditGrant | null;
  isSaving?: boolean;
  onCancel: () => void;
  onSubmit: (reason: string) => Promise<void>;
}

export function VoidCreditGrantModal({ open, grant, isSaving, onCancel, onSubmit }: VoidCreditGrantModalProps) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    setReason('');
    onCancel();
  };

  const handleSubmit = async () => {
    if (!reason.trim()) return;
    setError(null);
    try {
      await onSubmit(reason);
      setReason('');
    } catch (err) {
      setError(errMessage(err, t('billing.creditGrants.voidError')));
    }
  };

  if (!grant) return null;

  return (
    <FormDialog
      open={open}
      onClose={close}
      onSubmit={handleSubmit}
      submitLabel={t('billing.creditGrants.void')}
      canSubmit={!!reason.trim()}
      isSubmitting={isSaving}
      dirty={!!reason.trim()}
      aria-label={t('billing.creditGrants.void')}
    >
      <div className="space-y-3 px-3.5 pb-4 pt-2">
        <h3 className="text-base font-semibold text-foreground">{t('billing.creditGrants.void')}</h3>
        <p className="text-sm text-muted-foreground">{t('billing.creditGrants.voidConfirmDesc', { amount: formatMoney(grant.remaining) })}</p>
        {error && <p className="text-sm text-destructive">{error}</p>}

        <Input label={t('billing.creditGrants.voidReason')} value={reason} onChange={(e) => setReason(e.target.value)} />
      </div>
    </FormDialog>
  );
}

export default VoidCreditGrantModal;
